import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import * as PropTypes from 'prop-types'
import styles from './Tooltip.module.scss'
import ReactPortal from '../ReactPortal'
import useWindowSize from '../../utils/useWindowSize'
import useWindowScroll from '../../utils/useWindowScroll'
import OutsideClickHandler from 'react-outside-click-handler'
import Typography from '../Typography'

const SCREEN_PADDING = 10

const Tooltip = ({
  className = '',
  activeClassName = '',
  tooltipClassName = '',
  arrow = true,
  title = '',
  actionButton = null,
  children = null,
  placement = 'top',
  enterDelay = 250, //ms
  leaveDelay = 250, //ms
  property = {
    in: { opacity: 1 },
    out: { opacity: 0 },
  },
  transitionProperty = { in: 'opacity', out: 'opacity' },
  transitionDuration = { in: 300, out: 300 },
  transitionTimingFunction = { in: 'ease', out: 'ease' },
  shown,
  style = {},
  textAlign = 'left',
}) => {
  const triggerRef = useRef(null)
  const tooltipRef = useRef(null)
  const arrowRef = useRef(null)

  const [trigger, setTrigger] = useState(false)
  const [isShown, setShown] = useState(false)
  const [isClicked, setClicked] = useState(false)
  const [active, setActive] = useState(false)
  const [placementSuffix, setPlacementSuffix] = useState(placement)

  const horizontalPositions = ['left', 'right']

  const { width: windowWidth, height: windowHeight } = useWindowSize(300)
  const { x: scrollX, y: scrollY } = useWindowScroll(300)
  const [tooltipCSS, setTooltipCSS] = useState({ top: 0, left: 0 })
  const [arrowCSS, setArrowCSS] = useState({})

  const direction = useMemo(() => (trigger ? 'in' : 'out'), [trigger])

  const handleToggle = useCallback((state) => {
    setActive(state)

    if (state) {
      setShown(true)
    }
  }, [])

  const handleClick = useCallback((state) => {
    if (state) {
      setClicked(state)
    }
    handleToggle(state)
  }, [])

  useEffect(() => {
    if (typeof shown !== 'boolean') {
      return
    }
    if (shown) {
      setClicked(shown)
    }
    handleToggle(shown)
  }, [shown])

  useEffect(() => {
    setTrigger(active)
  }, [active])

  const handleTransitionEnd = useCallback(() => {
    if (!trigger && isShown) {
      setShown(false)
      setClicked(false)
    }
  }, [trigger, isShown])

  useEffect(() => {
    if (!trigger) {
      return
    }

    const {
      top: triggerTop = 0,
      left: triggerLeft = 0,
      width: triggerWidth = 0,
      height: triggerHeight = 0,
    } = triggerRef.current?.getBoundingClientRect() ?? {}

    const { width: arrowWidth = 0, height: arrowHeight = 0 } =
      arrowRef.current?.getBoundingClientRect() ?? {}

    const { width: tooltipWidth, height: tooltipHeight } =
      tooltipRef.current?.getBoundingClientRect() ?? {}

    let top = 0
    let left = 0
    let suffix = placementSuffix

    const coords = {
      top: {
        y: triggerTop + scrollY - tooltipHeight - arrowHeight,
        x: triggerLeft + scrollX + triggerWidth / 2 - tooltipWidth / 2,
        y2: triggerTop - arrowHeight + scrollY,
        x2: triggerLeft + triggerWidth / 2 - arrowWidth / 2 + scrollX,
      },
      bottom: {
        y: triggerTop + scrollY + triggerHeight + arrowHeight,
        x: triggerLeft + scrollX + triggerWidth / 2 - tooltipWidth / 2,
        y2: triggerTop + triggerHeight + scrollY,
        x2: triggerLeft + triggerWidth / 2 - arrowWidth / 2 + scrollX,
      },
      left: {
        y: triggerTop + scrollY + triggerHeight / 2 - tooltipHeight / 2,
        x: triggerLeft + scrollX - tooltipWidth - arrowWidth,
        y2: triggerTop + triggerHeight / 2 - arrowHeight / 2 + scrollY,
        x2: triggerLeft - arrowWidth + scrollX,
      },
      right: {
        y: triggerTop + scrollY + triggerHeight / 2 - tooltipHeight / 2,
        x: triggerLeft + scrollX + triggerWidth + arrowWidth,
        y2: triggerTop + triggerHeight / 2 - arrowHeight / 2 + scrollY,
        x2: triggerLeft + triggerWidth + scrollX,
      },
    }

    if (
      horizontalPositions.includes(suffix) &&
      tooltipWidth + triggerWidth > windowWidth - SCREEN_PADDING
    ) {
      // если ширина тултипа больше ширины окна браузера - принудительно устанавливаем положение "top"
      setPlacementSuffix('top')
      return
    }

    switch (suffix) {
      default:
      case 'top': {
        top = coords.top.y
        left = coords.top.x

        if (top < scrollY - SCREEN_PADDING) {
          // top = coords.bottom.y + arrowHeight
          setPlacementSuffix('bottom')
          return
        }

        if (left < scrollX + SCREEN_PADDING) {
          // в случае если тултип ушёл за левый край браузера - фиксируем его слева от триггера
          left = scrollX + triggerLeft - arrowWidth / 2
        } else if (
          left + tooltipWidth >
          scrollX + windowWidth - SCREEN_PADDING
        ) {
          // в случае если тултип ушёл за правый край браузера - фиксируем его справа от триггера
          left =
            triggerLeft + scrollX - tooltipWidth + triggerWidth + arrowWidth / 2
        }

        break
      }
      case 'bottom': {
        top = coords.bottom.y
        left = coords.bottom.x

        if (top + tooltipHeight > scrollY + windowHeight - SCREEN_PADDING) {
          // top = coords.top.y - arrowHeight
          setPlacementSuffix('top')
          return
        }

        if (left < scrollX + SCREEN_PADDING) {
          // в случае если тултип ушёл за левый край браузера - фиксируем его слева от триггера
          left = +scrollX + triggerLeft - arrowWidth / 2
        } else if (
          left + tooltipWidth >
          scrollX + windowWidth - SCREEN_PADDING
        ) {
          // в случае если тултип ушёл за правый край браузера - фиксируем его справа от триггера
          left =
            triggerLeft + scrollX - tooltipWidth + triggerWidth + arrowWidth / 2
        }

        break
      }
      case 'left': {
        top = coords.left.y
        left = coords.left.x

        if (left < scrollX + SCREEN_PADDING) {
          // left = coords.right.x + arrowWidth
          setPlacementSuffix('right')
          return
        }

        if (top < scrollY + SCREEN_PADDING) {
          // в случае если тултип ушёл за верхний край браузера - фиксируем его сверху от триггера
          top = triggerTop + scrollY - arrowHeight
        } else if (
          top + tooltipHeight >
          scrollY + windowHeight - SCREEN_PADDING
        ) {
          // в случае если тултип ушёл за нижний край браузера - фиксируем его снизу от триггера
          top =
            triggerTop + scrollY - tooltipHeight + triggerHeight + arrowHeight
        }

        break
      }
      case 'right': {
        top = coords.right.y
        left = coords.right.x

        if (left + tooltipWidth > scrollX + windowWidth - SCREEN_PADDING) {
          // left = coords.left.x - arrowWidth
          setPlacementSuffix('left')
          return
        }

        if (top < scrollY + SCREEN_PADDING) {
          // в случае если тултип ушёл за верхний край браузера - фиксируем его сверху от триггера
          top = triggerTop + scrollY - arrowHeight
        } else if (
          top + tooltipHeight >
          scrollY + windowHeight - SCREEN_PADDING
        ) {
          // в случае если тултип ушёл за нижний край браузера - фиксируем его снизу от триггера
          top =
            triggerTop + scrollY - tooltipHeight + triggerHeight + arrowHeight
        }

        break
      }
    }

    setArrowCSS({
      top: coords[placementSuffix].y2,
      left: coords[placementSuffix].x2,
    })

    setTooltipCSS((state) => ({
      ...state,
      top,
      left:
        left < SCREEN_PADDING /*|| tooltipWidth + left > windowWidth */
          ? SCREEN_PADDING
          : left,
    }))
  }, [
    scrollY,
    scrollX,
    windowWidth,
    windowHeight,
    trigger,
    placementSuffix,
    triggerRef,
    arrowRef,
  ])

  useEffect(() => {
    setPlacementSuffix(placement)
  }, [placement])

  useEffect(() => {
    setTooltipCSS((state) => ({
      ...state,
      ...property[direction],
      transitionDuration: transitionDuration[direction] + 'ms',
      transitionProperty: transitionProperty[direction],
      transitionTimingFunction: transitionTimingFunction[direction],
      transitionDelay:
        (isClicked ? 0 : trigger ? enterDelay : leaveDelay) + 'ms',
    }))
  }, [direction, trigger])

  return (
    <>
      <div
        ref={triggerRef}
        className={clsx(styles['tooltip-trigger'], className, {
          [activeClassName]: active,
        })}
        onMouseEnter={() => handleToggle(true)}
        onMouseLeave={() => handleToggle(false)}
        onClick={() => handleClick(true)}
      >
        {children}
      </div>

      {isShown && (
        <ReactPortal wrapperId="react-portal-tooltip-container">
          <OutsideClickHandler onOutsideClick={() => handleClick(false)}>
            <>
              <div
                className={clsx(
                  styles['tooltip'],
                  styles[`tooltip_placement-${placementSuffix}`],
                  tooltipClassName,
                )}
                ref={tooltipRef}
                style={{ ...tooltipCSS, ...style }}
                onTransitionEnd={handleTransitionEnd}
                onMouseEnter={() => handleToggle(true)}
                onMouseLeave={() => handleToggle(false)}
              >
                <div className={styles['tooltip__tip']}>
                  <div
                    className={clsx(
                      styles['tooltip__content'],
                      styles[`tooltip__content_text-align-${textAlign}`],
                    )}
                  >
                    <Typography
                      component={'div'}
                      variant={'Body-20'}
                      color={'Label-Onsurface'}
                    >
                      <span dangerouslySetInnerHTML={{ __html: title }} />
                    </Typography>
                    {actionButton && (
                      <div
                        className={styles['tooltip__action-button-container']}
                      >
                        {actionButton}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {arrow && (
                <div
                  ref={arrowRef}
                  className={clsx(
                    styles['tooltip-arrow'],
                    styles[`tooltip-arrow_${placementSuffix}`],
                  )}
                  style={{ ...tooltipCSS, ...arrowCSS, ...style }}
                />
              )}
            </>
          </OutsideClickHandler>
        </ReactPortal>
      )}
    </>
  )
}

Tooltip.propTypes = {
  className: PropTypes.string,
  activeClassName: PropTypes.string,
  tooltipClassName: PropTypes.string,
  arrow: PropTypes.bool,
  enterDelay: PropTypes.number,
  leaveDelay: PropTypes.number,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.oneOf([null])]),
  placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  transition: PropTypes.string,
  actionButton: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.element,
    PropTypes.arrayOf(PropTypes.element),
  ]),
  property: PropTypes.shape({
    in: PropTypes.object,
    out: PropTypes.object,
  }),
  transitionProperty: PropTypes.shape({
    in: PropTypes.object,
    out: PropTypes.object,
  }),
  transitionDuration: PropTypes.shape({
    in: PropTypes.object,
    out: PropTypes.object,
  }),
  transitionTimingFunction: PropTypes.shape({
    in: PropTypes.object,
    out: PropTypes.object,
  }),
  shown: PropTypes.bool,
  style: PropTypes.object,
  textAlign: PropTypes.oneOf(['left', 'center', 'right']),
}

export default Tooltip
